import React, { useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, DocumentIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../../config/api'

function fmtDateTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}

function fmtBytes(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—'
  const b = Number(n)
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

function lifecycleBadge(row) {
  if (row.purged_at) {
    return <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Purged</span>
  }
  if (row.expires_at && new Date(row.expires_at) < new Date()) {
    return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Expired</span>
  }
  if (row.verification_status === 'VERIFIED') {
    return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Verified</span>
  }
  if (row.verification_status === 'FAILED') {
    return <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Failed</span>
  }
  return <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Active</span>
}

const DOCUMENT_TYPES = [
  '',
  'ID_FRONT',
  'ID_BACK',
  'SELFIE',
  'PATRON_PHOTO',
  'SIGNATURE',
  'EVIDENCE_PHOTO',
  'INCIDENT_PHOTO',
  'WATCHLIST_PHOTO',
  'OTHER',
]

const SOURCES = [
  '',
  'MOBILE_PAYOUT',
  'DESKTOP_KYC',
  'INCIDENT_REPORT',
  'CASE_EVIDENCE',
  'WATCHLIST',
  'MANUAL_UPLOAD',
]

export default function TenantDocumentRegistryTab({ groupId }) {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 })
  const [tableMissing, setTableMissing] = useState(false)
  const [loading, setLoading] = useState(true)

  const [documentType, setDocumentType] = useState('')
  const [source, setSource] = useState('')
  const [search, setSearch] = useState('')
  const [includePurged, setIncludePurged] = useState(false)
  const [page, setPage] = useState(0)

  const limit = 50

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    try {
      const offset = page * limit
      const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) })
      if (documentType) qs.set('document_type', documentType)
      if (source) qs.set('source', source)
      if (search.trim()) qs.set('q', search.trim())
      if (includePurged) qs.set('include_purged', 'true')

      const [statsRes, listRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/document-registry/stats`)),
        authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/document-registry?${qs}`)),
      ])
      const statsJson = await statsRes.json().catch(() => ({}))
      const listJson = await listRes.json().catch(() => ({}))
      if (!statsRes.ok) throw new Error(statsJson.error || `Stats HTTP ${statsRes.status}`)
      if (!listRes.ok) throw new Error(listJson.error || `List HTTP ${listRes.status}`)

      setStats(statsJson.data || null)
      setRows(Array.isArray(listJson.data) ? listJson.data : [])
      setPagination(listJson.pagination || { total: 0, limit, offset })
      setTableMissing(!!(statsJson.data?.tableMissing || listJson.table_missing))
    } catch (e) {
      toast.error(e.message || 'Failed to load document registry')
    } finally {
      setLoading(false)
    }
  }, [groupId, page, documentType, source, search, includePurged])

  useEffect(() => {
    load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / limit))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Document registry</h2>
          <p className="text-sm text-gray-600 max-w-2xl">
            Central index of uploaded images and documents for this tenant — blob path, linked entity, retention, and purge status.
            New mobile payout uploads register here automatically after migration 163.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {tableMissing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The <code className="font-mono text-xs">document_registry</code> table is not provisioned for this tenant.
          Run tenant migration <strong>163_document_registry_pg.sql</strong> from the Overview tab or deploy with{' '}
          <code className="font-mono text-xs">RUN_DB_MIGRATIONS=1 TENANT_MIGRATE={groupId}</code>.
        </div>
      )}

      {stats && !tableMissing && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Active', value: stats.active, tone: 'text-teal-800' },
            { label: 'Total (incl. purged)', value: stats.total, tone: 'text-gray-900' },
            { label: 'Purged', value: stats.purged, tone: 'text-gray-600' },
            { label: 'Expired (pending purge)', value: stats.expired_pending_purge, tone: 'text-amber-700' },
          ].map((c) => (
            <div key={c.label} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.tone}`}>{c.value ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end rounded-lg border border-gray-200 bg-gray-50 p-3">
        <label className="text-xs text-gray-600">
          Type
          <select
            value={documentType}
            onChange={(e) => { setPage(0); setDocumentType(e.target.value) }}
            className="mt-1 block rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          >
            <option value="">All types</option>
            {DOCUMENT_TYPES.filter(Boolean).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-600">
          Source
          <select
            value={source}
            onChange={(e) => { setPage(0); setSource(e.target.value) }}
            className="mt-1 block rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          >
            <option value="">All sources</option>
            {SOURCES.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-600 flex-1 min-w-[200px]">
          Search
          <div className="mt-1 relative">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-2" />
            <input
              type="search"
              value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value) }}
              placeholder="Entity ID, patron, blob path…"
              className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm"
            />
          </div>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-1.5">
          <input
            type="checkbox"
            checked={includePurged}
            onChange={(e) => { setPage(0); setIncludePurged(e.target.checked) }}
          />
          Include purged
        </label>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-gray-500">Loading registry…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 mb-2" />
            <p className="text-sm">No documents registered yet.</p>
            {!tableMissing && (
              <p className="text-xs text-gray-400 mt-1">Submit a mobile payout with ID photos to create the first entries.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Registered</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Source</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Entity</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Patron</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Storage</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Size</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Retention</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/80">
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{fmtDateTime(row.registered_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.document_type}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{row.source}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.entity_type ? (
                        <span>
                          <span className="text-gray-500">{row.entity_type}</span>
                          {row.entity_id && (
                            <span className="block font-mono text-gray-800 truncate max-w-[140px]" title={row.entity_id}>
                              {row.entity_id}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{row.master_profile_id || '—'}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-600 max-w-[220px] truncate" title={row.storage_location}>
                      {row.storage_location || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{fmtBytes(row.file_size_bytes)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      <div>{row.retention_policy || '—'}</div>
                      {row.expires_at && (
                        <div className="text-gray-400">until {fmtDateTime(row.expires_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">{lifecycleBadge(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && pagination.total > limit && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {pagination.offset + 1}–{Math.min(pagination.offset + rows.length, pagination.total)} of {pagination.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border px-3 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="py-1">Page {page + 1} / {totalPages}</span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
